import React from 'react';

import { cva } from 'class-variance-authority';
import { cn } from './utils';

import type { VariantProps } from 'class-variance-authority';

export const TypographyVariants = cva([], {
  variants: {
    font: {
      sans: 'font-sans',
      mono: 'font-mono',
    },
    size: {
      xs: 'text-[12px] leading-[20px]',
      sm: 'text-sm leading-[20px]',
      base: 'text-base leading-[24px]',
      md: 'text-md leading-[28px]',
      lg: 'text-lg leading-[28px]',
      xl: 'text-xl leading-[28px]',
      '2xl': 'text-2xl leading-[32px]',
      '3xl': 'text-3xl leading-[36px]',
      '4xl': 'text-4xl',
      '5xl': 'text-5xl leading-[48px]',
      '6xl': 'text-6xl',
    },
    weight: {
      normal: 'font-normal',
      medium: 'font-medium',
      semiBold: 'font-semibold',
      bold: 'font-bold',
    },
  },
});

export type TextTagName = 'h1' | 'h2' | 'h3' | 'p' | 'span' | 'button' | 'a';

export type TextProps<T extends TextTagName = 'span'> = React.ComponentProps<T> &
  VariantProps<typeof TypographyVariants> & {
    as?: T;
    font?: VariantProps<typeof TypographyVariants>['font'];
    size?: VariantProps<typeof TypographyVariants>['size'];
    weight?: VariantProps<typeof TypographyVariants>['weight'];
  };

export const Text = React.forwardRef(
  <T extends TextTagName = 'span'>(
    { as = 'span' as T, font, size = 'base', weight = 'medium', className, children, ...props }: TextProps<T>,
    ref: React.ForwardedRef<T>,
  ) => {
    const Comp = as as React.ElementType;
    const defaultStyles = TypographyVariants({ font, size, weight });
    return (
      <Comp ref={ref} className={cn('text-text-primary', 'whitespace-pre-line', defaultStyles, className)} {...props}>
        {children}
      </Comp>
    );
  },
);

Text.displayName = 'Text';
